import {IconButton} from '@wandb/components';
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {buildAssembledPrompt} from '../../agentChat/promptAssembly';
import {envIsDev} from '../../config';
import {CodeSnippet} from '../CodeSnippet';
import {useNavContext} from '../NavContextProvider';
import {buildPromptPartsFromSlate} from './utils';
import {useWBAgentContextStore} from './WBAgentContextStore';

interface DebugViewState {
  open: boolean;
  toggle: () => void;
}

const WBAgentDebugViewContext = createContext<DebugViewState>({
  open: false,
  toggle: () => {},
});

/**
 * WBAgentDebugViewProvider holds the agent's debug view context and shares it
 * with the toggle button and the view slot.
 */
export const WBAgentDebugViewProvider = ({children}: {children: ReactNode}) => {
  const [open, setOpen] = useState(false);
  const state = useMemo<DebugViewState>(
    () => ({open, toggle: () => setOpen(v => !v)}),
    [open]
  );

  return (
    <WBAgentDebugViewContext.Provider value={state}>
      {children}
    </WBAgentDebugViewContext.Provider>
  );
};

/**
 * WBAgentDebugViewToggle is a dev-only button that toggles the agent's debug
 * view.
 */
export const WBAgentDebugViewToggle = () => {
  const {open, toggle} = useContext(WBAgentDebugViewContext);

  return envIsDev ? (
    <IconButton
      variant="ghost"
      icon="bug"
      isActive={open}
      aria-label="toggle assembled-prompt debug"
      onClick={toggle}
      tooltip={{children: open ? 'Show messages' : 'Show assembled prompt'}}
    />
  ) : null;
};

/**
 * WBAgentDebugViewSlot renders the debug view in place of `children` (e.g.
 * thread messages) while the debug view is open, and the children otherwise.
 */
export const WBAgentDebugViewSlot = ({children}: {children: ReactNode}) => {
  const {open} = useContext(WBAgentDebugViewContext);
  return open ? <WBAgentDebugView /> : <>{children}</>;
};

const WBAgentDebugView = () => (
  <div className="flex-1 p-8">
    <WBAgentAssembledPromptView />
  </div>
);

/**
 * WBAgentAssembledPromptView renders a JSON view of the assembled prompt that
 * would be sent to the wb_agent backend to create the next turn.
 */
const WBAgentAssembledPromptView = () => {
  const {params} = useNavContext();
  const [json, setJson] = useState('');

  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    let raf = 0;

    const evaluate = () => {
      const {entityName, projectName} = paramsRef.current;
      const {composerDraft, selectedRuns} = useWBAgentContextStore.getState();

      const userParts = buildPromptPartsFromSlate(composerDraft, selectedRuns, {
        entityName: entityName ?? '',
        projectName: projectName ?? '',
      });
      const prompt = buildAssembledPrompt(userParts, paramsRef.current);

      // Re-render only when the assembled prompt actually changes.
      const next = JSON.stringify(prompt, null, 2);
      setJson(prev => (prev === next ? prev : next));

      raf = requestAnimationFrame(evaluate);
    };

    evaluate();
    return () => cancelAnimationFrame(raf);
  }, []);

  return <CodeSnippet codeSnippet={json} codeLanguage="json" />;
};
